import React from 'react';
import { BRAND, VIEW_LABELS, type TicketView } from './statuses';
import type { TicketStats } from './ticketApi';

interface Props {
  activeView: TicketView;
  onViewChange: (v: TicketView) => void;
  stats: TicketStats | null;
}

const VIEW_ORDER: TicketView[] = [
  'inbox',
  'mine',
  'new',
  'pending',
  'on_hold',
  'unassigned',
  'solved',
  'all',
];

function countForView(view: TicketView, stats: TicketStats | null): number | null {
  if (!stats) return null;
  switch (view) {
    case 'inbox':
      return (
        stats.total -
        (stats.by_status.closed || 0)
      );
    case 'mine':
      return null; // Depends on current admin — skip count for simplicity
    case 'new':
      return stats.new_count;
    case 'pending':
      return stats.pending_count;
    case 'on_hold':
      return stats.by_status.on_hold || 0;
    case 'solved':
      return stats.by_status.solved || 0;
    case 'unassigned':
      return stats.unassigned_count;
    case 'all':
      return stats.total;
    default:
      return null;
  }
}

export const TicketFiltersSidebar: React.FC<Props> = ({ activeView, onViewChange, stats }) => {
  return (
    <div
      style={{
        width: 220,
        flexShrink: 0,
        padding: '20px 12px',
        borderRight: `1px solid ${BRAND.border}`,
        background: BRAND.surfaceAlt,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: BRAND.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          padding: '0 12px 10px 12px',
        }}
      >
        Views
      </div>

      {VIEW_ORDER.map((view) => {
        const meta = VIEW_LABELS[view];
        const count = countForView(view, stats);
        const isActive = activeView === view;
        return (
          <button
            key={view}
            onClick={() => onViewChange(view)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              border: 'none',
              background: isActive ? BRAND.surface : 'transparent',
              color: isActive ? BRAND.green : BRAND.textSecondary,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              cursor: 'pointer',
              marginBottom: 2,
              textAlign: 'left',
              transition: 'background 0.12s',
              boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.background = 'var(--surface-slate)';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <meta.icon size={16} />
              <span>{meta.label}</span>
            </span>
            {count !== null && count > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isActive ? BRAND.green : BRAND.textTertiary,
                  background: isActive ? 'var(--surface-mint)' : BRAND.surfaceMuted,
                  padding: '1px 8px',
                  borderRadius: 100,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}

      <div
        style={{
          marginTop: 24,
          padding: '0 12px',
          fontSize: 11,
          color: BRAND.textMuted,
          lineHeight: 1.5,
        }}
      >
        Tip: click a ticket row to open the full conversation view.
      </div>
    </div>
  );
};
